import { Link } from "@remix-run/react";
import { ExpirationOptions, type ExpirationType } from "@repo/api/src/schemas/order.schema";
import { type FormEvent, useState } from "react";
import { ContentPage } from "../components/layout/ContentPage";
import { useTranslation } from "../i18n";

const copy = {
	ja: {
		title: "Google Driveから共有する",
		lead: "Driveの共有リンクを貼ると、ファイルをGSplat Shareへ取り込んで共有リンクを作ります。",
		link: "Google Driveのファイルリンク",
		hint: "「リンクを知っている全員」が閲覧・ダウンロードできる単一ファイルが対象です。PLY・SPZ・SOG・Viewer AppのHTML、最大2 GB。フォルダやログインが必要なファイルには対応していません。",
		name: "共有ページのタイトル（省略可）",
		description: "説明（省略可）",
		expiration: "公開期限",
		password: "閲覧パスワード（省略可）",
		submit: "Driveから取り込む",
		busy: "Driveから取得し、保存しています。この画面を開いたままお待ちください。",
		done: "共有リンクができました。",
		open: "共有ページを確認",
		copy: "リンクをコピー",
		copied: "コピーしました",
		working: "取り込み中…",
		again: "別のリンクを取り込む",
		back: "ファイルを選んで共有する",
	},
	en: {
		title: "Import from Google Drive",
		lead: "Paste a Drive sharing link to copy the file into GSplat Share and create a share link.",
		link: "Google Drive file link",
		hint: "The file must allow anyone with the link to view and download it. Single PLY, SPZ, SOG, or Viewer App HTML files up to 2 GB only. Folders and files requiring sign-in are not supported.",
		name: "Share title (optional)",
		description: "Description (optional)",
		expiration: "Link expiration",
		password: "Viewer password (optional)",
		submit: "Import from Drive",
		busy: "Fetching and saving the file. Keep this page open.",
		done: "Your share link is ready.",
		open: "Open and check the share",
		copy: "Copy link",
		copied: "Copied",
		working: "Importing…",
		again: "Import another link",
		back: "Upload a local file",
	},
} as const;

const expirationOptions: { value: ExpirationType; ja: string; en: string }[] = [
	{ value: ExpirationOptions.HOUR, ja: "1時間", en: "1 hour" },
	{ value: ExpirationOptions.DAY, ja: "1日", en: "1 day" },
	{ value: ExpirationOptions.WEEK, ja: "1週間", en: "1 week" },
	{ value: ExpirationOptions.UNLIMITED, ja: "無期限", en: "No expiration" },
];

export default function DriveImportPage() {
	const { locale, localizedPath } = useTranslation();
	const words = copy[locale];
	const [url, setUrl] = useState("");
	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [password, setPassword] = useState("");
	const [expirationType, setExpirationType] = useState<ExpirationType>(ExpirationOptions.WEEK);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const [shareId, setShareId] = useState("");
	const [copied, setCopied] = useState(false);
	const submit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (busy) return;
		setBusy(true);
		setError("");
		try {
			const response = await fetch("/share/import/drive", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				credentials: "same-origin",
				body: JSON.stringify({
					url: url.trim(),
					title: title.trim() || undefined,
					description: description.trim() || undefined,
					password: password || undefined,
					expirationType,
				}),
			});
			const result = (await response.json()) as { shareId?: string; error?: string };
			if (!response.ok || !result.shareId)
				throw new Error(result.error || (locale === "ja" ? "取り込みに失敗しました。" : "Import failed."));
			setShareId(result.shareId);
			setCopied(false);
		} catch (cause) {
			setError(cause instanceof Error ? cause.message : String(cause));
		} finally {
			setBusy(false);
		}
	};
	const fieldClass = "mt-2 w-full rounded-md border border-border bg-background px-3 py-3 text-foreground";
	return (
		<ContentPage title={words.title} subtitle={words.lead}>
			<div className="not-prose max-w-2xl space-y-6">
				{shareId ? (
					<div className="space-y-4" aria-live="polite" aria-atomic="true">
						<h2 className="text-xl font-semibold">{words.done}</h2>
						<input
							readOnly
							aria-label="Share URL"
							className={fieldClass}
							value={`${typeof window === "undefined" ? "" : window.location.origin}/viewer/${shareId}`}
							onFocus={event => event.target.select()}
						/>
						{error && (
							<p role="alert" className="text-destructive">
								{error}
							</p>
						)}
						<div className="flex flex-wrap gap-5">
							<button
								type="button"
								className="text-primary underline"
								onClick={async () => {
									try {
										await navigator.clipboard.writeText(`${window.location.origin}/viewer/${shareId}`);
										setCopied(true);
									} catch {
										setError(
											locale === "ja" ? "URL欄を選択してコピーしてください。" : "Select the URL above to copy it.",
										);
									}
								}}
							>
								{copied ? words.copied : words.copy}
							</button>
							<a className="text-primary underline" href={`/viewer/${shareId}`}>
								{words.open} ↗
							</a>
							<button type="button" className="text-primary underline" onClick={() => setShareId("")}>
								{words.again}
							</button>
						</div>
					</div>
				) : (
					<form onSubmit={submit} className="space-y-5">
						<label className="block font-medium">
							{words.link}
							<input
								className={fieldClass}
								type="url"
								required
								value={url}
								onChange={event => setUrl(event.target.value)}
								placeholder="https://drive.google.com/file/d/..."
							/>
						</label>
						<p className="text-sm leading-relaxed text-muted-foreground">{words.hint}</p>
						<label className="block font-medium">
							{words.name}
							<input
								className={fieldClass}
								maxLength={100}
								value={title}
								onChange={event => setTitle(event.target.value)}
							/>
						</label>
						<label className="block font-medium">
							{words.description}
							<textarea
								className={fieldClass}
								maxLength={1000}
								value={description}
								onChange={event => setDescription(event.target.value)}
							/>
						</label>
						<label className="block font-medium">
							{words.expiration}
							<select
								className={fieldClass}
								value={expirationType}
								onChange={event => setExpirationType(event.target.value as ExpirationType)}
							>
								{expirationOptions.map(option => (
									<option key={option.value} value={option.value}>
										{option[locale]}
									</option>
								))}
							</select>
						</label>
						<label className="block font-medium">
							{words.password}
							<input
								className={fieldClass}
								type="password"
								minLength={3}
								maxLength={200}
								value={password}
								onChange={event => setPassword(event.target.value)}
							/>
						</label>
						{error && (
							<p role="alert" className="text-destructive">
								{error}
							</p>
						)}
						{busy && (
							<p aria-live="polite" aria-atomic="true" className="text-sm text-muted-foreground">
								{words.busy}
							</p>
						)}
						<button
							type="submit"
							disabled={busy}
							className="rounded-md bg-primary px-5 py-3 font-semibold text-primary-foreground disabled:opacity-50"
						>
							{busy ? words.working : words.submit}
						</button>
					</form>
				)}
				<Link className="inline-block text-sm text-primary underline" to={localizedPath("/")}>
					← {words.back}
				</Link>
			</div>
		</ContentPage>
	);
}
